import moment from 'moment';

/**
 * Backend `Contrato.version_actual` is `ContratoVersion.id` (PK), not the
 * business version number stored in `ContratoVersion.version`.
 * See `Contrato.get_current_version_id()` / `ContratoSerializer.get_version_actual`.
 */

/** Find the version row whose primary key matches `versionActualId`. */
export const findContratoVersionByActualId = (versions, versionActualId) => {
    if (!Array.isArray(versions) || versions.length === 0) return null;
    if (versionActualId == null || versionActualId === 0) return null;
    return versions.find((v) => v.id === versionActualId) ?? null;
};

/** Index of the version row for `versionActualId`, or -1 when not found. */
export const findContratoVersionIndexByActualId = (versions, versionActualId) => {
    if (!Array.isArray(versions) || versions.length === 0) return -1;
    if (versionActualId == null || versionActualId === 0) return -1;
    return versions.findIndex((v) => v.id === versionActualId);
};

/** Index of the latest created business version, falling back to the last row. */
export const findLatestContratoVersionIndex = (versions) => {
    if (!Array.isArray(versions) || versions.length === 0) return -1;

    let latestIndex = -1;
    let latestVersion = null;
    versions.forEach((version, index) => {
        const rawVersion = version?.version;
        if (rawVersion == null || rawVersion === '') return;
        const businessVersion = Number(rawVersion);
        if (!Number.isFinite(businessVersion)) return;
        if (
            latestIndex === -1 ||
            businessVersion > latestVersion ||
            (businessVersion === latestVersion &&
                Number(version.id) > Number(versions[latestIndex].id))
        ) {
            latestIndex = index;
            latestVersion = businessVersion;
        }
    });

    return latestIndex >= 0 ? latestIndex : versions.length - 1;
};

export const getNextContratoVersionNumber = (versions) => {
    const latestIndex = findLatestContratoVersionIndex(versions);
    const latestVersion = Number(versions?.[latestIndex]?.version);

    return Number.isFinite(latestVersion) ? latestVersion + 1 : 1;
};

export const getLatestPersistedBusinessVersion = (versions) =>
    (versions ?? [])
        .filter((version) => version?.id != null && Number.isFinite(Number(version.version)))
        .reduce((latest, version) => {
            const versionNumber = Number(version.version);
            const latestNumber = Number(latest?.version);
            if (!latest || versionNumber > latestNumber) return version;
            if (versionNumber === latestNumber && Number(version.id) > Number(latest.id)) {
                return version;
            }
            return latest;
        }, null);

export const getNewContratoVersionMinimumStartDate = (versions, now = moment()) => {
    const today = moment(now).startOf('day');
    const previousVersion = getLatestPersistedBusinessVersion(versions);
    const previousStart = moment(previousVersion?.vigencia_desde, 'YYYY-MM-DD', true);
    const dayAfterPrevious = previousStart.isValid() ? previousStart.add(1, 'day') : null;

    return (dayAfterPrevious?.isAfter(today) ? dayAfterPrevious : today).format('YYYY-MM-DD');
};

export const getContratoVersionStartDateMinimum = (version, versions, now = moment()) => {
    if (version?.id) return null;
    return getNewContratoVersionMinimumStartDate(versions, now);
};

export const isNewContratoVersionStartDateValid = (startDate, versions, now = moment()) => {
    const start = moment(startDate, 'YYYY-MM-DD', true);
    const minimum = moment(
        getNewContratoVersionMinimumStartDate(versions, now),
        'YYYY-MM-DD',
        true,
    );

    return start.isValid() && start.isSameOrAfter(minimum);
};

/**
 * Resolve the contract's current version for Adelanto send and related flows.
 * Payment flows must resolve only backend `version_actual` (ContratoVersion id).
 * Unsaved drafts deliberately return null because they cannot receive NEX.
 */
export const resolveCurrentContratoVersion = (versions, versionActualId) => {
    if (!Array.isArray(versions) || versions.length === 0) return null;
    const byId = findContratoVersionByActualId(versions, versionActualId);
    if (byId) return byId;
    return null;
};

export const hasExactlyOneAdelantoBenefit = (version) =>
    (version?.contrato_v_beneficio ?? []).length === 1;

export const isContractTypeSelectorDisabled = (contractId, contractTypeId) =>
    !!contractId || (contractTypeId != null && contractTypeId !== '');

export const resolveAdelantoProductSelection = (families, productId) => {
    const family = families?.find((item) =>
        item.products?.some((product) => product.id === productId),
    );
    const product = family?.products?.find((item) => item.id === productId);
    return family && product ? { family, product } : null;
};

/**
 * Prefer the version the operator is viewing in the picker; fall back to
 * backend `version_actual` when the index is out of range.
 */
export const resolveSelectedContratoVersion = (versions, selectedIndex, versionActualId) => {
    if (!Array.isArray(versions) || versions.length === 0) return null;
    if (selectedIndex >= 0 && selectedIndex < versions.length) {
        return versions[selectedIndex];
    }
    return resolveCurrentContratoVersion(versions, versionActualId);
};
